import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Document text extraction, in the browser.
 *
 * Extracted from AICVIngestion.jsx so the recruiter bulk upload, candidate
 * onboarding, the public application form and My Details all share one
 * implementation. Extraction stays client-side so anonymous applicants are
 * supported and only plain text crosses the wire.
 */

export const SUPPORTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt'];

export const MAX_CV_FILE_BYTES = 10 * 1024 * 1024; // 10MB

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

/**
 * Validate a CV file before upload or extraction.
 *
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateDocumentFile(file) {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  const extension = file.name.toLowerCase().split('.').pop();
  if (!SUPPORTED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: 'Please upload a PDF, DOC, DOCX or TXT file',
    };
  }

  // Browsers do not always populate file.type, so treat an empty type as unknown
  // rather than invalid and fall back to the extension check above.
  if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Please upload a PDF, DOC, DOCX or TXT file',
    };
  }

  if (file.size > MAX_CV_FILE_BYTES) {
    return {
      valid: false,
      error: `File is too large. Maximum size is ${MAX_CV_FILE_BYTES / (1024 * 1024)}MB`,
    };
  }

  return { valid: true };
}

/**
 * Extract plain text from a PDF, DOC/DOCX or TXT file.
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function extractDocumentText(file) {
  const extension = file.name.toLowerCase().split('.').pop();

  try {
    if (extension === 'txt') {
      return await file.text();
    }

    if (extension === 'pdf') {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    }

    if (extension === 'doc' || extension === 'docx') {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    }

    throw new Error(`Unsupported file type: ${extension}`);
  } catch (error) {
    console.error(`Error extracting text from ${file.name}:`, error);
    throw new Error(
      `Failed to extract text from ${extension.toUpperCase()} file: ${error.message}`
    );
  }
}

export default extractDocumentText;
